import React, { useRef, useState, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, CheckCircle, AlertCircle } from 'lucide-react';

export interface UploadFile {
  id: string;
  file: File;
  preview: string;
  status: 'uploading' | 'success' | 'error';
  progress: number;
}

export interface IdCardUploadProps {
  label: string;
  description?: string;
  accept?: string;
  maxSize?: number;
  onFileChange?: (file: File | null) => void;
}

export const IdCardUpload: React.FC<IdCardUploadProps> = ({
  label,
  description,
  accept = 'image/*',
  maxSize = 5,
  onFileChange,
}) => {
  const [uploadedFile, setUploadedFile] = useState<UploadFile | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File) => {
    setError(null);

    if (!file.type.startsWith('image/')) {
      setError('Vui lòng chọn file ảnh');
      return;
    }

    if (file.size > maxSize * 1024 * 1024) {
      setError(`Kích thước file không được vượt quá ${maxSize}MB`);
      return;
    }

    const newFile: UploadFile = {
      id: Date.now().toString(),
      file,
      preview: URL.createObjectURL(file),
      status: 'uploading',
      progress: 0,
    };

    setUploadedFile(newFile);
    onFileChange?.(file);

    let progress = 0;
    const interval = setInterval(() => {
      progress += 20;
      if (progress >= 100) {
        clearInterval(interval);
        setUploadedFile((prev) =>
          prev ? { ...prev, status: 'success', progress: 100 } : null
        );
      } else {
        setUploadedFile((prev) =>
          prev ? { ...prev, progress } : null
        );
      }
    }, 200);
  }, [maxSize, onFileChange]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  }, [handleFile]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleRemove = () => {
    if (uploadedFile) {
      URL.revokeObjectURL(uploadedFile.preview);
    }
    setUploadedFile(null);
    setError(null);
    onFileChange?.(null);
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleClick = () => {
    inputRef.current?.click();
  };

  return (
    <div className="w-full">
      <label className="block !text-sm !font-medium !text-gray-700 dark:!text-dark-text-secondary mb-2">
        {label}
      </label>
      
      {!uploadedFile ? (
        <div
          onClick={handleClick}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative flex flex-col items-center justify-center w-full h-48 !border-2 !border-dashed !rounded-xl cursor-pointer !transition-colors ${
            dragActive
              ? '!border-primary !bg-primary/5'
              : '!border-gray-300 dark:!border-dark-border hover:!border-primary dark:hover:!border-primary !bg-gray-50 dark:!bg-dark-card-elevated'
          }`}
        >
          <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={handleChange}
            className="hidden"
          />
          <div className="flex flex-col items-center justify-center p-6 text-center">
            <div className="p-3 !bg-primary/10 rounded-full mb-3">
              <Upload className="w-6 h-6 !text-primary" />
            </div>
            <p className="!text-sm !font-medium !text-gray-700 dark:!text-dark-text mb-1">
              Kéo thả hoặc nhấn để tải ảnh lên
            </p>
            {description && (
              <p className="!text-xs !text-gray-500 dark:!text-dark-text-muted">
                {description}
              </p>
            )}
            <p className="!text-xs !text-gray-400 dark:!text-dark-text-muted mt-2">
              PNG, JPG tối đa {maxSize}MB
            </p>
          </div>
        </div>
      ) : (
        <div className="relative w-full !border !border-gray-200 dark:!border-dark-border !rounded-xl overflow-hidden !bg-gray-50 dark:!bg-dark-card-elevated">
          <div className="relative h-48 flex items-center justify-center p-4">
            <img
              src={uploadedFile.preview}
              alt="Preview"
              className="max-h-full max-w-full object-contain rounded-lg"
            />
            
            <button
              onClick={handleRemove}
              className="absolute top-2 right-2 p-1.5 !bg-red-500 hover:!bg-red-600 !rounded-full !transition-colors !border-none"
            >
              <X className="w-4 h-4 !text-white" />
            </button>

            {uploadedFile.status === 'uploading' && (
              <div className="absolute inset-0 !bg-black/40 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-1 !bg-gray-200 !rounded-full overflow-hidden mb-2">
                    <div
                      className="h-full !bg-primary !transition-all"
                      style={{ width: `${uploadedFile.progress}%` }}
                    />
                  </div>
                  <p className="!text-xs !text-white">{uploadedFile.progress}%</p>
                </div>
              </div>
            )}
          </div>

          <div className="px-4 py-3 !border-t !border-gray-200 dark:!border-dark-border flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ImageIcon className="w-5 h-5 !text-gray-500 dark:!text-dark-text-muted" />
              <div className="min-w-0">
                <p className="!text-sm !font-medium !text-gray-700 dark:!text-dark-text truncate max-w-[200px]">
                  {uploadedFile.file.name}
                </p>
                <p className="!text-xs !text-gray-500 dark:!text-dark-text-muted">
                  {(uploadedFile.file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            {uploadedFile.status === 'success' && (
              <CheckCircle className="w-5 h-5 !text-status-success" />
            )}
            {uploadedFile.status === 'error' && (
              <AlertCircle className="w-5 h-5 !text-status-error" />
            )}
          </div>
        </div>
      )}

      {error && (
        <p className="mt-2 !text-xs !text-status-error flex items-center gap-1">
          <AlertCircle className="w-3.5 h-3.5" />
          {error}
        </p>
      )}
    </div>
  );
};
